/**
 * Browser-side auth client.
 *
 * The PC / mobile main bundles call `getSession()` whenever they're
 * about to persist a recording, and `requireSignIn()` to redirect the
 * user through LINE Login when no session is on file. Both functions
 * share a 30-second in-memory cache so a single Record click does not
 * hammer /api/auth/get-session three times.
 *
 * better-auth ships a typed client (`createAuthClient`), but for
 * BodyNodeLink we only need three endpoints — keep this surface tiny
 * to avoid pulling the full client into every entry bundle.
 */

export interface Session {
  user: {
    id: string;
    name: string;
    email: string | null;
    image: string | null;
  };
  session: {
    expiresAt: number;
  };
}

let cached: { session: Session | null; fetchedAtMs: number } | null = null;
const CACHE_TTL_MS = 30_000;

/** Returns the current session, or null when unauthenticated. */
export async function getSession(opts: { force?: boolean } = {}): Promise<Session | null> {
  const now = Date.now();
  if (!opts.force && cached && now - cached.fetchedAtMs < CACHE_TTL_MS) {
    return cached.session;
  }
  try {
    const res = await fetch('/api/auth/get-session', {
      method: 'GET',
      credentials: 'same-origin',
    });
    if (!res.ok) {
      cached = { session: null, fetchedAtMs: now };
      return null;
    }
    const data = (await res.json()) as Session | { user: null } | null;
    const session = data && 'user' in data && data.user ? (data as Session) : null;
    cached = { session, fetchedAtMs: now };
    return session;
  } catch {
    cached = { session: null, fetchedAtMs: now };
    return null;
  }
}

/**
 * Send the user through LINE Login. Returns a promise that never
 * resolves locally — the browser navigates away to LINE.
 */
export async function signInWithLine(callbackURL: string): Promise<never> {
  const res = await fetch('/api/auth/sign-in/social', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ provider: 'line', callbackURL }),
  });
  if (!res.ok) {
    throw new Error(`sign-in/social failed: HTTP ${res.status}`);
  }
  const { url } = (await res.json()) as { url: string };
  // Invalidate the cache so the post-callback page sees the fresh
  // session immediately.
  cached = null;
  window.location.href = url;
  // Returning a never-resolving promise prevents callers from
  // continuing after the redirect — they would already be navigated
  // away by the time microtasks ran, but explicit is clearer.
  return new Promise(() => {}) as Promise<never>;
}

/** Clear the session cookie and the in-memory cache. */
export async function signOut(): Promise<void> {
  await fetch('/api/auth/sign-out', {
    method: 'POST',
    credentials: 'same-origin',
  });
  cached = null;
}

/**
 * Gate a save action. Returns true when the user has an active
 * session; returns false (and triggers the LINE Login flow) when
 * they don't. The caller is expected to bail out of the save when
 * the return is false — the browser navigates away during sign-in
 * so any work scheduled after this call is effectively cancelled.
 */
export async function requireSignIn(callbackURL: string): Promise<boolean> {
  const s = await getSession();
  if (s) return true;
  await signInWithLine(callbackURL);
  return false;
}
